import React, { useState } from "react";
import { func, string, object } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { WithFacebookLogin, WithGoogleLogin } from "@quintype/components";

import Headline from "../../atoms/Headline";
import ProthomFamilyView from "../../atoms/ProthomFamilyView";
import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import SocialAuthButton from "../../atoms/SocialAuthButton";
import WithSSO from "../../with-sso";

import { login } from "../../helper/api"; // eslint-disable-line
import { isValidEmail, getCurrentSite } from "../../utils/utils";
import { setMember, setLoading } from "../../helper/actions";
import { staticTexts } from "../../utils/Translate";

import "./login.m.css";

const Login = ({
  setLoading,
  googleClientId,
  facebookId,
  currentPath,
  sketchesHost,
  authHost,
  publisherAttributes
}) => {
  const [user, setUser] = useState({
    email: "",
    password: ""
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const lang = getCurrentSite(currentPath);

  const setData = e => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    // If success message is set remove it since data is changed
    if (successMessage) {
      setSuccessMessage(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = event => {
    event.preventDefault();

    if (validate()) {
      // true -> field errors, false - no fields errors
      return;
    }

    setLoading(true);

    const userObj = {
      username: user.email,
      password: user.password
    };

    login(userObj, callbackUrl, redirectUrl, authHost)
      .then(response => {
        window.location.href = response["redirect-url"];
      })
      .catch(err => setError(err.message))
      .finally(setLoading(false));
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    if (!updatedUser.email) {
      updatedUser.emailError = "Email field cannot be left blank!";
      flag = true;
    } else if (!isValidEmail(updatedUser.email)) {
      updatedUser.emailError = "Invalid Email !!";
      flag = true;
    } else {
      updatedUser.emailError = "";
    }

    if (!updatedUser.password) {
      updatedUser.passwordError = "Password field cannot be left blank!";
      flag = true;
    } else {
      updatedUser.passwordError = "";
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  return (
    <form onSubmit={onSubmitClick}>
      <div styleName="container">
        <div styleName="wrapper">
          <Headline text="Login" headerLevel={3} headlineType={3} />
          <WithFacebookLogin sso={true} redirectUrl={redirectUrl} appId={facebookId} scope="email" emailMandatory>
            {({ login, serverSideLoginPath }) => <SocialAuthButton type="fb" href={serverSideLoginPath} />}
          </WithFacebookLogin>

          <WithGoogleLogin sso={true} redirectUrl={redirectUrl} clientId={googleClientId} scope="email" emailMandatory>
            {({ login, serverSideLoginPath }) => (
              <SocialAuthButton type="google" className="google" href={serverSideLoginPath} />
            )}
          </WithGoogleLogin>

          <span styleName="or-text">OR</span>
          <TextField
            value={user.email}
            error={user.emailError}
            placeholder="Email"
            type="email"
            inputType="type2"
            name="email"
            onChange={setData}
            required
          />
          <PasswordField
            value={user.password}
            error={user.passwordError}
            placeholder="Password"
            name="password"
            onChange={setData}
            required
          />

          {error && <MessageView type="error" text={staticTexts(lang).invalidUsernameOrPassword} />}

          <Button content="LOGIN" type="submit" buttonType="type3" />
          <a href="forgot-password" styleName="forgot-password">
            Forgot password
          </a>

          <WithSSO ssoHost={authHost} signUpPath="/sign-up-page" redirectUrl={redirectUrl} callbackUrl={callbackUrl}>
            {({ signUpHref }) => (
              <span styleName="new-user">
                New to Prothom Alo?
                <a href={signUpHref}>Create an account</a>
              </span>
            )}
          </WithSSO>
        </div>

        <ProthomFamilyView />
      </div>
    </form>
  );
};

Login.propTypes = {
  setLoading: func,
  member: object,
  navigateToPage: func,
  setMember: func,
  checkForMemberUpdated: func,
  googleClientId: string,
  facebookId: string,
  setUser: func,
  currentPath: string,
  sketchesHost: string,
  authHost: string,
  publisherAttributes: object
};

const mapStateToProps = state => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  member: state.member || null,
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {})
});

const mapDispatchToProps = dispatch => ({
  setLoading: isLoading => dispatch(setLoading(isLoading)),
  navigateToPage: url => global.app.navigateToPage(dispatch, url),
  setMember: member => dispatch(setMember(member))
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
